import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ArrowsLeftRight, HardHat, CalendarBlank, CheckCircle, Warning, Pause } from "@phosphor-icons/react";

/**
 * Dialog to reassign a user's active workload (future interventions + active chantiers)
 * to another teammate — typically triggered right before pausing them.
 *
 * Props:
 *   open, onOpenChange: standard
 *   sourceUser: user being paused
 *   team: full list of users (used to build target selector)
 *   pauseAfter: if true, will also flip is_active=false on success
 *   onDone: called after successful reassignment (parent should reload team + toast)
 */
export default function ReassignWorkloadDialog({ open, onOpenChange, sourceUser, team, pauseAfter = true, onDone }) {
  const [workload, setWorkload] = useState({ interventions: [], chantiers: [], counts: { interventions: 0, chantiers: 0 } });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [targetId, setTargetId] = useState("");
  const [ivSelected, setIvSelected] = useState(new Set());
  const [chSelected, setChSelected] = useState(new Set());
  const [removeSource, setRemoveSource] = useState(true);

  const targets = useMemo(
    () => (team || []).filter((u) => u.id !== sourceUser?.id && u.is_active !== false && u.role !== "compta"),
    [team, sourceUser]
  );

  useEffect(() => {
    if (!open || !sourceUser) return;
    setLoading(true);
    setTargetId("");
    api
      .get(`/users/${sourceUser.id}/active-workload`)
      .then((r) => {
        setWorkload(r.data);
        setIvSelected(new Set((r.data.interventions || []).map((i) => i.id)));
        setChSelected(new Set((r.data.chantiers || []).map((c) => c.id)));
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [open, sourceUser]);

  const toggle = (setter) => (id) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const totalSelected = ivSelected.size + chSelected.size;
  const nothingToDo = workload.counts.interventions === 0 && workload.counts.chantiers === 0;

  const submit = async () => {
    if (!nothingToDo && !targetId) {
      toast.error("Choisis un équipier cible");
      return;
    }
    setSaving(true);
    try {
      if (!nothingToDo) {
        await api.post(`/users/${sourceUser.id}/reassign-workload`, {
          target_user_id: targetId,
          intervention_ids: Array.from(ivSelected),
          chantier_ids: Array.from(chSelected),
          remove_source_from_chantiers: removeSource,
          deactivate_source: pauseAfter,
        });
      } else if (pauseAfter) {
        await api.patch(`/users/${sourceUser.id}`, { is_active: false });
      }
      onOpenChange(false);
      setTimeout(() => {
        if (nothingToDo) {
          toast.success(pauseAfter ? `${sourceUser.name} mis en pause` : "Aucun chantier à réattribuer");
        } else {
          const target = targets.find((t) => t.id === targetId);
          toast.success(
            `Réattribué à ${target?.name || "l'équipier"}${pauseAfter ? " · " + sourceUser.name + " mis en pause" : ""}`
          );
        }
      }, 50);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#111] border-white/10 text-white rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="reassign-workload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <ArrowsLeftRight size={20} className="text-amber-400" weight="duotone" />
            Réattribuer avant mise en pause
          </DialogTitle>
          <DialogDescription className="text-xs text-white/60 mt-1">
            {sourceUser?.name} a des chantiers/interventions à venir. Transfère-les à un autre équipier avant de le mettre en pause.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-white/50">Chargement…</div>
        ) : nothingToDo ? (
          <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-4 text-sm text-emerald-300 flex items-start gap-2">
            <CheckCircle size={18} weight="duotone" className="shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">Aucune tâche à réattribuer</div>
              <div className="text-xs text-emerald-200/70 mt-0.5">Cet équipier n&apos;a ni intervention future ni chantier actif. Tu peux le mettre en pause en toute sécurité.</div>
            </div>
          </div>
        ) : (
          <>
            {/* Target picker */}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block">Nouveau responsable</label>
              <select
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                className="w-full h-11 rounded-xl bg-white/5 border border-white/10 px-3 text-sm focus:outline-none focus:border-[#005CFF]"
                data-testid="reassign-target-select"
              >
                <option value="">— Choisir un équipier —</option>
                {targets.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.role})
                  </option>
                ))}
              </select>
              {targets.length === 0 && (
                <div className="text-[11px] text-amber-400 mt-1 flex items-center gap-1"><Warning size={12} /> Aucun autre équipier actif disponible.</div>
              )}
            </div>

            {/* Interventions */}
            {workload.counts.interventions > 0 && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-xs font-semibold text-white/80 flex items-center gap-1.5">
                    <CalendarBlank size={14} className="text-[#7CB2FF]" weight="duotone" />
                    Interventions à venir ({workload.counts.interventions})
                  </div>
                  <button
                    onClick={() => setIvSelected(ivSelected.size === workload.interventions.length ? new Set() : new Set(workload.interventions.map((i) => i.id)))}
                    className="text-[10px] uppercase tracking-widest text-white/50 hover:text-white"
                    data-testid="toggle-all-interventions"
                  >
                    {ivSelected.size === workload.interventions.length ? "Rien" : "Tout"}
                  </button>
                </div>
                <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                  {workload.interventions.map((iv) => {
                    const on = ivSelected.has(iv.id);
                    return (
                      <button
                        key={iv.id}
                        onClick={() => toggle(setIvSelected)(iv.id)}
                        className={`w-full text-left flex items-start gap-2 p-2 rounded-lg border ${on ? "bg-[#005CFF]/15 border-[#005CFF]/40" : "bg-white/5 border-white/10 hover:border-white/25"}`}
                        data-testid={`iv-toggle-${iv.id}`}
                      >
                        <div className={`w-4 h-4 rounded flex-shrink-0 mt-0.5 flex items-center justify-center ${on ? "bg-[#005CFF]" : "bg-white/10"}`}>
                          {on && <CheckCircle size={12} weight="fill" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">{iv.title}</div>
                          <div className="text-[11px] text-white/50 truncate">
                            {fmtDate(iv.scheduled_date)} · {iv.chantier_name || "sans chantier"}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Chantiers */}
            {workload.counts.chantiers > 0 && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-xs font-semibold text-white/80 flex items-center gap-1.5">
                    <HardHat size={14} className="text-amber-400" weight="duotone" />
                    Chantiers actifs ({workload.counts.chantiers})
                  </div>
                  <button
                    onClick={() => setChSelected(chSelected.size === workload.chantiers.length ? new Set() : new Set(workload.chantiers.map((c) => c.id)))}
                    className="text-[10px] uppercase tracking-widest text-white/50 hover:text-white"
                    data-testid="toggle-all-chantiers"
                  >
                    {chSelected.size === workload.chantiers.length ? "Rien" : "Tout"}
                  </button>
                </div>
                <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                  {workload.chantiers.map((c) => {
                    const on = chSelected.has(c.id);
                    return (
                      <button
                        key={c.id}
                        onClick={() => toggle(setChSelected)(c.id)}
                        className={`w-full text-left flex items-start gap-2 p-2 rounded-lg border ${on ? "bg-amber-500/15 border-amber-500/40" : "bg-white/5 border-white/10 hover:border-white/25"}`}
                        data-testid={`ch-toggle-${c.id}`}
                      >
                        <div className={`w-4 h-4 rounded flex-shrink-0 mt-0.5 flex items-center justify-center ${on ? "bg-amber-500" : "bg-white/10"}`}>
                          {on && <CheckCircle size={12} weight="fill" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">{c.name}</div>
                          <div className="text-[11px] text-white/50 truncate">{c.address || "—"} · statut : {c.status}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <label className="flex items-center gap-2 mt-2 text-[11px] text-white/70 cursor-pointer">
                  <Switch checked={removeSource} onCheckedChange={setRemoveSource} data-testid="remove-source-switch" />
                  Retirer {sourceUser?.name?.split(" ")[0] || "l'équipier"} de la liste des assignés
                </label>
              </div>
            )}

            <div className="text-[11px] text-white/40 mt-2">
              {totalSelected} élément(s) sélectionné(s) · les techs concernés recevront une notification.
            </div>
          </>
        )}

        <div className="flex gap-2 mt-3 sticky bottom-0 bg-[#111] pt-2">
          <Button variant="ghost" className="flex-1 border border-white/10" onClick={() => onOpenChange(false)} data-testid="reassign-cancel">
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={saving || loading || (!nothingToDo && !targetId)}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
            data-testid="reassign-confirm"
          >
            {saving ? "…" : nothingToDo ? (pauseAfter ? <><Pause size={14} weight="fill" /> Mettre en pause</> : "OK") : pauseAfter ? "Réattribuer & Mettre en pause" : "Réattribuer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
